import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Alert,
  Grid,
  Switch,
} from "@mui/material";
import { themes } from "services/constants";
import { stickyHeader } from "services/constants";

export const CurrentTemplate = ({
  currentTemplateData,
  revisionBasedOn,
  isEdit,
  isHistoryScreen = false,
}) => {
  const DataTable = ({
    data,
    columns,
    emptyMessage = "No data available",
    renderRow,
    summaryRow,
  }) => (
    <TableContainer sx={{ mt: 1, backgroundColor: themes?.whiteColor }}>
      <Table size="small" className="border">
        <TableHead sx={stickyHeader}>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell key={index}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        {!data || data.length === 0 ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <Alert severity="info">{emptyMessage}</Alert>
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {data.map((item, index) => renderRow(item, index))}
            {summaryRow && summaryRow()}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );

  const isOverride =
    currentTemplateData?.override_count ?? currentTemplateData?.over_ride;

  return (
    <Box
      sx={{
        padding: 2,
      }}
    >
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        {isEdit && (
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Switch
              checked={isOverride}
              disabled={isOverride}
              onChange={() => {}}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: themes?.primary,
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#000",
                  opacity: 1,
                },
                "& .MuiSwitch-switchBase": {
                  color: "#bdbdbd",
                },
                "& .MuiSwitch-track": {
                  backgroundColor: "#e0e0e0",
                  opacity: 1,
                },
              }}
              onClick={(e) => e.preventDefault()}
            />
            <Typography variant="subtitle2" fontWeight={600} fontSize={20}>
              Overridden applied
            </Typography>
          </Box>
        )}

        {revisionBasedOn === "Template" ? (
          <Grid container spacing={4}>
            {[
              {
                label: "Override Employees",
                value: currentTemplateData?.override_count ?? "-",
              },
              {
                label: "Non Override Employees",
                value: currentTemplateData?.non_override_count ?? "-",
              },
              {
                label: "Total Employees",
                value: currentTemplateData?.total_employee_count ?? "-",
              },
              {
                label: "Salary Calculation Type",
                value: currentTemplateData?.salary_type ?? "-",
              },
              {
                label: "Salary Basis",
                value: currentTemplateData?.salary_basis ?? "-",
              },
              {
                label: "Amount",
                value: currentTemplateData?.salary ?? "-",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    fontSize={20}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ color: "#00d084" }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={4}>
            {[
              {
                label: isHistoryScreen
                  ? "Override Employee"
                  : "Current Salary Template",
                value: isHistoryScreen
                  ? currentTemplateData?.over_ride === true
                    ? "Yes"
                    : "No"
                  : (currentTemplateData?.template_name ?? "-"),
              },
              {
                label: "Salary Calculation Type",
                value: currentTemplateData?.salary_type ?? "-",
              },
              {
                label: "Salary Basis",
                value: currentTemplateData?.salary_basis ?? "-",
              },
              {
                label: "Amount",
                value: currentTemplateData?.salary ?? "-",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    fontSize={20}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ color: "#00d084" }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 3 }}>
        <Typography fontWeight={600}>Earnings</Typography>

        <DataTable
          data={currentTemplateData?.earnings}
          columns={["Component", "Method", "Computation", "Amount"]}
          renderRow={(data, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>{data?.component_name}</TableCell>
                <TableCell>{data?.computation_method}</TableCell>
                <TableCell>{data?.computation_value}</TableCell>
                <TableCell>{data?.amount ?? "0"}</TableCell>
              </TableRow>
            </React.Fragment>
          )}
          summaryRow={() => (
            <>
              <TableRow>
                <TableCell>
                  <strong>Total Earnings </strong>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <strong>{currentTemplateData?.total_earnings}</strong>
                </TableCell>
              </TableRow>
            </>
          )}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 3 }}>
        <Typography fontWeight={600}>Deductions</Typography>

        <DataTable
          data={currentTemplateData?.deduction}
          columns={["Component", "Method", "Computation", "Amount"]}
          renderRow={(data, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>{data?.component_name}</TableCell>
                <TableCell>{data?.computation_method}</TableCell>
                <TableCell>{data?.computation_value}</TableCell>
                <TableCell>{data?.amount ?? "0"}</TableCell>
              </TableRow>
            </React.Fragment>
          )}
          summaryRow={() => (
            <>
              <TableRow>
                <TableCell>
                  <strong>Total Deductions </strong>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <strong>{currentTemplateData?.total_deductions}</strong>
                </TableCell>
              </TableRow>
            </>
          )}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 3 }}>
        <Typography fontWeight={600}>Employer Contribution</Typography>

        <DataTable
          data={currentTemplateData?.employer_contribution}
          columns={["Component", "Method", "Computation", "Amount"]}
          renderRow={(data, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>{data?.component_name}</TableCell>
                <TableCell>{data?.computation_method}</TableCell>
                <TableCell>{data?.computation_value}</TableCell>
                <TableCell>{data?.amount ?? "0"}</TableCell>
              </TableRow>
            </React.Fragment>
          )}
          summaryRow={() => (
            <>
              <TableRow>
                <TableCell>
                  <strong>Total Employer Contribution </strong>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <strong>
                    {currentTemplateData?.total_employer_contribution}
                  </strong>
                </TableCell>
              </TableRow>
            </>
          )}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 3 }}>
        <Typography fontWeight={600}>Reimbursement</Typography>

        <DataTable
          data={currentTemplateData?.reimbursement}
          columns={["Component", "Method", "Computation", "Amount"]}
          renderRow={(data, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>{data?.component_name}</TableCell>
                <TableCell>{data?.computation_method}</TableCell>
                <TableCell>{data?.computation_value}</TableCell>
                <TableCell>{data?.amount ?? "0"}</TableCell>
              </TableRow>
            </React.Fragment>
          )}
          summaryRow={() => (
            <>
              <TableRow>
                <TableCell>
                  <strong>Total Reimbursement</strong>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <strong>{currentTemplateData?.total_reimbursement}</strong>
                </TableCell>
              </TableRow>
            </>
          )}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box
        sx={{
          mt: 3,
          p: 3,
          borderRadius: 2,
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>Salary Summary</Typography>

        <TableContainer>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{ border: "none", py: 1 }}
                ></TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 800,
                    border: "none",
                    pl: 0,
                    fontSize: "1.2rem",
                    color: "#2e7d32",
                  }}
                >
                  Total Cost to Company (CTC)
                </TableCell>
                <TableCell
                  sx={{
                    border: "none",
                    fontWeight: 800,
                    fontSize: "1.2rem",
                    color: "#2e7d32",
                  }}
                  align="right"
                >
                  {currentTemplateData?.total_ctc ?? 0}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: "none", pl: 0 }}>
                  Employer Contribution
                </TableCell>
                <TableCell sx={{ border: "none" }} align="right">
                  {currentTemplateData?.total_employer_contribution ?? 0}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: "none", pl: 0 }}>
                  Gross Salary
                </TableCell>
                <TableCell sx={{ border: "none" }} align="right">
                  {currentTemplateData?.total_salary ?? 0}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: "none", pl: 0 }}>
                  Deductions
                </TableCell>
                <TableCell sx={{ border: "none" }} align="right">
                  {currentTemplateData?.total_deductions ?? 0}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={2} sx={{ border: "none", py: 1 }}>
                  <Divider />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    border: "none",
                    pl: 0,
                    fontSize: "1.1rem",
                  }}
                >
                  Net Salary
                </TableCell>
                <TableCell
                  sx={{
                    border: "none",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                  }}
                  align="right"
                >
                  {currentTemplateData?.net_salary ?? 0}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
